import { useQuery } from "@apollo/client";
import { formatCountyDisplay } from "encamp-shared/src/utils/county";
import {
  EManifestWasteUnit,
  FacilityRcraExemptBoilerActivity,
  FacilityRcraRecognizedTraderActivity,
  FacilityRcraRecyclerActivity,
  FacilityRcraSlabActivity,
  FacilityRcraSubpartKEntityType,
  FacilityRcraSubpartPActivity,
  FacilityRcraTransporterActivity,
  FacilityRcraUniversalWasteType,
  FacilityRcraUsedOilMarketerActivity,
  FacilityRcraUsedOilProcessorActivity,
  FacilityRcraUsedOilTransporterActivity,
  RcraEpisodicProjectType,
  RcraHsmLandBasedUnit,
  RcraHsmNotificationReason,
  RcraInfoLandType,
  RcraLqgClosureType,
  SubmissionReasonForSubmittal,
} from "generated-graphql/graphql";

import { GET_RCRA_8700_12_SNAPSHOT_CONTENT_QUERY } from "./queries";

/**
 * Narrow, read-only shapes of the resolved 8700-12 snapshot content the frozen
 * stepper renders. The backend returns the snapshot as opaque JSON; the
 * editor-shaped payload lives under `resolved` (the `Resolved870012` produced by
 * `resolveRcra870012`). We model only the fields these views display rather than
 * depending on `encamp-snapshot` from the frontend, and assign the JSON into
 * these shapes instead of `as`-casting.
 */
export type ResolvedAddress = {
  streetAddress1?: string | null;
  streetAddress2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
};

export type ResolvedSiteLocation = ResolvedAddress & {
  county?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  useLatLongAsPrimary?: boolean | null;
};

/**
 * The §10a–§18 RCRA profile body, mirroring `ResolvedRcraProfileBody` in
 * `encamp-snapshot`. Typed with the generated enums so the same label maps the
 * live `RcraProfileSection` uses apply without casts.
 */
export type ResolvedRcraProfileBody = {
  shortTermGenerator?: boolean | null;
  shortTermGeneratorNotes?: string | null;
  tsdActivity?: boolean | null;
  offSiteReceipt?: boolean | null;
  recyclerActivity?: FacilityRcraRecyclerActivity | null;
  exemptBoilerActivities?: FacilityRcraExemptBoilerActivity[] | null;
  transporterActivity?: FacilityRcraTransporterActivity | null;
  undergroundInjectionControl?: boolean | null;
  usImporterOfHazardousWaste?: boolean | null;
  recognizedTraderActivity?: FacilityRcraRecognizedTraderActivity | null;
  slabImporter?: FacilityRcraSlabActivity | null;
  lqhuwActivities?: FacilityRcraUniversalWasteType[] | null;
  universalWasteDestFacility?: boolean | null;
  usedOilTransporterActivities?: FacilityRcraUsedOilTransporterActivity[] | null;
  usedOilProcessorActivities?: FacilityRcraUsedOilProcessorActivity[] | null;
  offSpecUsedOilBurner?: boolean | null;
  usedOilMarketerActivities?: FacilityRcraUsedOilMarketerActivity[] | null;
  subpartPActivity?: FacilityRcraSubpartPActivity | null;
  subpartPWithdrawal?: boolean | null;
  subpartKEntityTypes?: FacilityRcraSubpartKEntityType[] | null;
  subpartKWithdrawal?: boolean | null;
  // §14 LQG Consolidation of VSQG Hazardous Waste
  isLqgConsolidator?: boolean | null;
  consolidatedSites?: ResolvedConsolidatedSite[] | null;
  manifestBroker?: boolean | null;
  publicComments?: string | null;
};

/**
 * §14 — a VSQG consolidated site, mirroring `ResolvedConsolidatedSite` in
 * `encamp-snapshot`. The contact is a nested `ResolvedContact` resolved from the
 * site's materialized SubmissionContact.person (null when the site has no
 * contact), NOT inline `contact*` fields.
 */
export type ResolvedConsolidatedSite = {
  epaIdNumber?: string | null;
  siteName?: string | null;
  streetAddress1?: string | null;
  streetAddress2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
  contact?: ResolvedContact | null;
};

/** §13 episodic waste line, mirroring `ResolvedEpisodicWasteLine` in `encamp-snapshot`. */
export type ResolvedEpisodicWasteLine = {
  wasteDescription?: string | null;
  estimatedQuantity?: number | null;
  quantityUnit?: EManifestWasteUnit | null;
  federalWasteCodes?: string[] | null;
  stateWasteCodes?: string[] | null;
};

/**
 * §13 Episodic Generation event, mirroring `ResolvedEpisodicEvent` in
 * `encamp-snapshot`. The emergency contact is a nested `ResolvedContact`
 * resolved from the §13 SubmissionContact.person (null when none was filed),
 * NOT inline `emergencyContact*` fields.
 */
export type ResolvedEpisodicEvent = {
  isPlannedEpisodicEvent?: boolean | null;
  projectTypes?: RcraEpisodicProjectType[] | null;
  beginningDate?: string | null;
  endDate?: string | null;
  otherDescription?: string | null;
  emergencyContact?: ResolvedContact | null;
  wasteLines?: ResolvedEpisodicWasteLine[] | null;
};

/** §15 LQG Site Closure, mirroring `ResolvedSiteClosure` in `encamp-snapshot`. */
export type ResolvedSiteClosure = {
  closureType?: RcraLqgClosureType | null;
  expectedClosureDate?: string | null;
  requestingNewClosureDate?: string | null;
  actualClosureDate?: string | null;
};

/** §16 HSM activity row, mirroring `ResolvedHsmActivity` in `encamp-snapshot`. */
export type ResolvedHsmActivity = {
  facilityCode?: string | null;
  wasteCodes?: string[] | null;
  estimatedShortTons?: number | null;
  landBasedUnit?: RcraHsmLandBasedUnit | null;
};

/** §16 HSM Notification, mirroring `ResolvedHsmNotification` in `encamp-snapshot`. */
export type ResolvedHsmNotification = {
  isManagingHsm?: boolean | null;
  reason?: RcraHsmNotificationReason | null;
  effectiveDate?: string | null;
  activities?: ResolvedHsmActivity[] | null;
};

export type ResolvedContact = {
  first?: string | null;
  last?: string | null;
  name?: string | null;
  title?: string | null;
  email?: string | null;
  // A Person's stored phones, carried through the snapshot as a JSON array. Only
  // the number is displayed in the frozen filing-notifications panels.
  phones?: { number?: string | null }[] | null;
  reportingRoles?: string[] | null;
};

export type ResolvedEpaId = {
  epaId?: string | null;
};

export type ResolvedGeneratorStatus = {
  calculatedFederalGeneratorStatus?: string | null;
  calculatedStateGeneratorStatus?: string | null;
};

export type Resolved870012Content = {
  // §1 Reason for Submittal — submission-level, carried at the top of the snapshot.
  reasonForSubmittal?: SubmissionReasonForSubmittal | null;
  siteName?: { siteName?: string | null } | null;
  siteLocation?: ResolvedSiteLocation | null;
  siteMailingAddress?: ResolvedAddress | null;
  landType?: RcraInfoLandType | null;
  naics?: {
    primaryNaicsCode?: number | null;
    otherNaicsCodes?: number[] | null;
  } | null;
  rcraProfile?: ResolvedRcraProfileBody | null;
  contacts?: {
    siteContact?: ResolvedContact[] | null;
    owners?: ResolvedContact[] | null;
    operators?: ResolvedContact[] | null;
    additionalContacts?: ResolvedContact[] | null;
  } | null;
  epaIds?: ResolvedEpaId[] | null;
  generatorStatus?: ResolvedGeneratorStatus | null;
  wasteCodes?: {
    federalWasteCodes?: string[] | null;
    stateWasteCodes?: string[] | null;
  } | null;
  // Filing Notifications — null when the section's gate was not "Yes".
  episodicEvent?: ResolvedEpisodicEvent | null;
  siteClosure?: ResolvedSiteClosure | null;
  hsmNotification?: ResolvedHsmNotification | null;
};

/**
 * Raw state-field rows carried alongside `resolved` on the stored content. The
 * snapshot persists only `key`/`value` (the human label and input type come from
 * live field definitions, which are not snapshotted), so the frozen State Info
 * panel can show only key/value pairs.
 */
export type ResolvedStateField = {
  key?: string | null;
  value?: unknown;
};

export type SnapshotStoredContent = {
  resolved?: Resolved870012Content | null;
  stateFields?: ResolvedStateField[] | null;
} | null;

export const EMPTY = "—";

/**
 * Fetches and narrows the frozen 8700-12 snapshot content for a submission.
 * Returns both `resolved` (the form-shaped payload the per-section panels render)
 * and the top-level `content` (for raw `stateFields`). Returns nulls when no
 * snapshot exists yet.
 */
export function useFrozenSnapshotContent(submissionId: string) {
  const { data, loading, error } = useQuery(GET_RCRA_8700_12_SNAPSHOT_CONTENT_QUERY, {
    variables: { submissionId },
    skip: !submissionId,
    fetchPolicy: "cache-and-network",
  });

  const content: SnapshotStoredContent = data?.rcra870012SnapshotContent ?? null;
  return { content, resolved: content?.resolved ?? null, loading, error };
}

function oneLineAddress(addr: ResolvedAddress): string {
  const street = [addr.streetAddress1, addr.streetAddress2].filter(Boolean).join(" ");
  const cityState = [addr.city, addr.state].filter(Boolean).join(", ");
  return [street, cityState, addr.zip].filter(Boolean).join(" ");
}

function normalizeCountry(value: string): string {
  if (value === "US" || value === "USA") return "United States";
  return value
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(" ");
}

/**
 * Multi-line site location string mirroring `formatRcraSiteLocation` in the live
 * profile (address line, county, country, lat/long).
 */
export function formatResolvedSiteLocation(loc: ResolvedSiteLocation | null | undefined): string {
  if (!loc) return "";
  const lines: string[] = [];

  const oneLiner = oneLineAddress(loc);
  if (oneLiner) lines.push(oneLiner);
  if (loc.county) lines.push(formatCountyDisplay(loc.county));
  if (loc.country) lines.push(normalizeCountry(loc.country));

  if (loc.latitude != null || loc.longitude != null) {
    const lat = loc.latitude?.toString() ?? EMPTY;
    const lng = loc.longitude?.toString() ?? EMPTY;
    const primarySuffix = loc.useLatLongAsPrimary ? " (used as primary address)" : "";
    lines.push(`Lat/Long: ${lat}, ${lng}${primarySuffix}`);
  }

  return lines.join("\n");
}

/** Multi-line mailing-address string mirroring `formatRcraSiteMailingAddress`. */
export function formatResolvedMailingAddress(addr: ResolvedAddress | null | undefined): string {
  if (!addr) return "";
  const lines: string[] = [];
  const oneLiner = oneLineAddress(addr);
  if (oneLiner) lines.push(oneLiner);
  if (addr.country) lines.push(normalizeCountry(addr.country));
  return lines.join("\n");
}
